import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import { PlusCircle, Search } from 'lucide-react';
import CompanyModal from './CompanyModal';
import CompanyList from './CompanyList';

const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Initial data fetch
  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedCompany(null);
  };

  // Fetch companies data
  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${boardMemberData.restURL}board-member/v1/companies`, {
        headers: {
          'X-WP-Nonce': boardMemberData.nonce
        }
      });
      const data = await response.json();
      setCompanies(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch companies');
      setLoading(false);
    }
  };

  // Toggle company block status
  const handleToggleBlock = async (companyId) => {
    try {
      const response = await fetch(`${boardMemberData.restURL}board-member/v1/companies/${companyId}/toggle-block`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': boardMemberData.nonce
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to toggle company status');
      }

      // Preserve pagination while updating data
      await fetchCompanies();
    } catch (err) {
      setError(err.message || 'Failed to toggle company status');
    }
  };

  // Delete company
  const handleDelete = async (companyId) => {
    if (!window.confirm(__('Are you sure you want to delete this company?'))) {
      return;
    }

    try {
      const response = await fetch(`${boardMemberData.restURL}wp/v2/company/${companyId}`, {
        method: 'DELETE',
        headers: {
          'X-WP-Nonce': boardMemberData.nonce
        }
      });

      if (response.ok) {
        await fetchCompanies();
      }
    } catch (err) {
      setError('Failed to delete company');
    }
  };

  // Handle editing a company
  const handleEdit = (company) => {
    setSelectedCompany(company);
    setShowModal(true);
  };

  // Handle saving company data
  const handleSave = async (data) => {
    try {
      await fetchCompanies(); // Refresh data while maintaining pagination
      handleModalClose();
    } catch (error) {
      console.error('Error saving company:', error);
      setError('Failed to save company data');
    }
  };

  if (!boardMemberData.isAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">{__('Company Management')}</h1>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => {
              setSelectedCompany(null);
              setShowModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            {__('Add Company')}
          </button>
        </div>
      </div>

      <div className="mt-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder={__('Search companies...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-4 flex justify-center">
          <div className="loader">{__('Loading...')}</div>
        </div>
      ) : (
        <div className="mt-8">
          <CompanyList
            companies={companies}
            onToggleBlock={handleToggleBlock}
            onDelete={handleDelete}
            onEdit={handleEdit}
            searchTerm={searchTerm}
            pagination={pagination}
            setPagination={setPagination}
          />
        </div>
      )}

      {showModal && (
        <CompanyModal
          company={selectedCompany}
          onClose={handleModalClose}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default CompanyManagement;